/**
 * 历史记录 Hook
 * 本地存储实现，支持跨组件状态同步
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { getStorageItem, setStorageItem, StorageKeys } from '../utils/storage';

/**
 * 历史记录项
 */
export interface HistoryItem {
  id: string;
  name: string;
  title: string;
  icon: string;
  timestamp: number;
}

/**
 * 添加历史记录的输入类型（更宽松）
 */
export interface AddHistoryInput {
  id: string;
  name: string;
  title?: string;
  icon: string;
  viewCount?: number;
}

/**
 * 最大历史记录数
 */
const MAX_HISTORY = 7;

/**
 * 同步事件名
 */
const HISTORY_CHANGE_EVENT = 'frey-history-change';

/**
 * 将输入转换为HistoryItem
 */
function toHistoryItem(input: AddHistoryInput): HistoryItem {
  return {
    id: input.id,
    name: input.name,
    title: input.title || input.name,
    icon: input.icon,
    timestamp: Date.now(),
  };
}

/**
 * 从localStorage加载历史记录
 */
function loadHistory(): HistoryItem[] {
  if (typeof window === 'undefined') return [];
  return getStorageItem<HistoryItem[]>(StorageKeys.HISTORY, []);
}

/**
 * 保存到localStorage并触发同步事件
 */
function saveHistory(history: HistoryItem[]) {
  if (typeof window === 'undefined') return;
  setStorageItem(StorageKeys.HISTORY, history);
  // 触发自定义事件，通知其他组件
  window.dispatchEvent(new CustomEvent(HISTORY_CHANGE_EVENT, { detail: history }));
}

/**
 * 历史记录管理
 */
export function useHistory() {
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const isUpdatingRef = useRef(false);

  // 从localStorage加载
  useEffect(() => {
    setHistory(loadHistory());
    setIsLoaded(true);
  }, []);

  // 监听其他组件的历史记录变化
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleChange = (e: CustomEvent<HistoryItem[]>) => {
      if (!isUpdatingRef.current) {
        setHistory(e.detail);
      }
    };

    window.addEventListener(HISTORY_CHANGE_EVENT, handleChange as EventListener);
    return () => {
      window.removeEventListener(HISTORY_CHANGE_EVENT, handleChange as EventListener);
    };
  }, []);

  // 保存到localStorage（防抖）
  useEffect(() => {
    if (!isLoaded) return;
    
    const timer = setTimeout(() => {
      isUpdatingRef.current = true;
      saveHistory(history);
      setTimeout(() => { isUpdatingRef.current = false; }, 0);
    }, 50);

    return () => clearTimeout(timer);
  }, [history, isLoaded]);

  /**
   * 添加历史记录
   */
  const addHistory = useCallback((input: AddHistoryInput) => {
    setHistory(prev => {
      const item = toHistoryItem(input);
      // 去重并添加到头部
      const filtered = prev.filter(h => h.id !== item.id);
      const newHistory = [item, ...filtered].slice(0, MAX_HISTORY);
      return newHistory;
    });
  }, []);

  /**
   * 移除历史记录
   */
  const removeHistory = useCallback((id: string) => {
    setHistory(prev => prev.filter(h => h.id !== id));
  }, []);

  /**
   * 清空历史记录
   */
  const clearHistory = useCallback(() => {
    setHistory([]);
    // 立即触发同步事件
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(HISTORY_CHANGE_EVENT, { detail: [] }));
    }
  }, []);

  /**
   * 检查是否在历史记录中
   */
  const isInHistory = useCallback((id: string) => {
    return history.some(h => h.id === id);
  }, [history]);

  return {
    history,
    isLoaded,
    addHistory,
    removeHistory,
    clearHistory,
    isInHistory,
  };
}

/**
 * 添加历史记录（React Query风格）
 */
export function useAddHistory() {
  const { addHistory } = useHistory();
  
  return {
    mutate: addHistory,
    isPending: false,
    isError: false,
  };
}
